import type { ReportKind } from '@stayafrica/contracts';
import type { ReportGroupBy, ReportScope } from '@/features/analytics/queries';

/**
 * Catalogue des 12 rapports (FR-DSH-06, SCR-100 à SCR-104 ; `EXPENSES` depuis
 * le sprint 2.9, D-052).
 *
 * Chaque entrée décrit la famille d'accueil, la permission requise, les
 * regroupements proposés et le filtre de statut pertinent. Le serveur reste
 * l'autorité : une famille masquée ici répond de toute façon 403.
 *
 * Règles d'autorisation, copiées de `GET /reports/:kind`
 * (`apps/api/src/modules/analytics`) :
 *   - `report.operational` ouvre la route et suffit aux rapports
 *     d'exploitation (`OCCUPANCY`, `RESERVATIONS`) ;
 *   - `report.financial` est exigé **en plus** pour les neuf autres ;
 *   - `report.export` est exigé en plus pour `format=csv`.
 *
 * Les regroupements listés sont exactement ceux que le serveur honore : un
 * `groupBy` inconnu n'est pas rejeté par l'API mais retombe sur le défaut, et un
 * menu qui ne change rien tromperait l'utilisateur. De même, seul le rapport
 * `RESERVATIONS` applique le filtre `status`.
 *
 * Hypothèse **A-050** (à confirmer par Bertrand) : les rapports « Clients » et
 * « Entreprises » exposent le chiffre d'affaires par client et relèvent donc de
 * `report.financial`, et non de `report.operational`.
 */

export const REPORT_FAMILIES = ['ACTIVITY', 'FINANCE', 'CUSTOMERS', 'RESOURCES'] as const;
export type ReportFamily = (typeof REPORT_FAMILIES)[number];

/** Permission qui ouvre la route `GET /reports/:kind`, quel que soit le rapport. */
export const REPORTS_ENTRY_PERMISSION = 'report.operational';

export interface ReportDefinition {
  kind: ReportKind;
  family: ReportFamily;
  /**
   * Permission propre au rapport. `report.operational` est de toute façon
   * requise pour ouvrir la route (voir `canOpenReport`).
   */
  permission: 'report.operational' | 'report.financial';
  /** Regroupements honorés par le serveur ; le premier est la valeur par défaut. Vide = aucun. */
  groupBy: ReportGroupBy[];
  /** Valeurs du filtre de statut (vide = le serveur n'applique pas ce filtre). */
  statuses: string[];
  /** Espace de noms i18n des libellés de statut. */
  statusNamespace?: 'reservations' | 'billing' | 'payments';
  /**
   * Périmètres honorés (rapport `RESERVATIONS`, D-053) ; le premier est la
   * valeur par défaut. Vide = le serveur n'applique pas ce paramètre.
   */
  scopes?: ReportScope[];
  /** Affiche une bande de KPI en tête (totaux monétaires). */
  showTotalsHeader: boolean;
}

export const REPORTS: ReportDefinition[] = [
  {
    kind: 'RESERVATIONS',
    family: 'ACTIVITY',
    permission: 'report.operational',
    groupBy: [],
    statuses: [
      'PENDING_CONFIRMATION',
      'OPTION',
      'CONFIRMED',
      'CHECKED_IN',
      'CHECKED_OUT',
      'CANCELLED',
      'NO_SHOW',
    ],
    statusNamespace: 'reservations',
    scopes: ['ENGAGED', 'ALL', 'CANCELLED'],
    showTotalsHeader: true,
  },
  {
    kind: 'OCCUPANCY',
    family: 'ACTIVITY',
    permission: 'report.operational',
    groupBy: ['DAY', 'RESOURCE', 'RESOURCE_TYPE'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'REVENUE',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: ['DAY', 'WEEK', 'MONTH', 'RESOURCE', 'SOURCE'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'PAYMENTS',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: ['METHOD', 'DAY', 'WEEK', 'MONTH'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'RECEIVABLES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'DISCOUNTS',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'TAXES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'CASH',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'EXPENSES',
    family: 'FINANCE',
    permission: 'report.financial',
    groupBy: ['DAY', 'WEEK', 'MONTH', 'METHOD', 'CATEGORY'],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'CUSTOMERS',
    family: 'CUSTOMERS',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'COMPANIES',
    family: 'CUSTOMERS',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
  {
    kind: 'RESOURCE_PERFORMANCE',
    family: 'RESOURCES',
    permission: 'report.financial',
    groupBy: [],
    statuses: [],
    showTotalsHeader: true,
  },
];

const BY_SLUG = new Map(REPORTS.map((report) => [report.kind.toLowerCase(), report]));

/** `revenue`, `resource_performance` → définition du rapport, ou `undefined`. */
export function reportBySlug(slug: string): ReportDefinition | undefined {
  return BY_SLUG.get(slug.toLowerCase());
}

/**
 * Vrai si l'utilisateur peut ouvrir ce rapport : la permission d'entrée de la
 * route **et** celle du rapport (identiques pour un rapport d'exploitation).
 */
export function canOpenReport(
  report: ReportDefinition,
  can: (permission: string) => boolean,
): boolean {
  return can(REPORTS_ENTRY_PERMISSION) && can(report.permission);
}

/** Rapports d'une famille, visibles pour l'utilisateur courant. */
export function reportsOfFamily(
  family: ReportFamily,
  can: (permission: string) => boolean,
): ReportDefinition[] {
  return REPORTS.filter((report) => report.family === family && canOpenReport(report, can));
}
